import { EVENT_QUEUE_MAX_EVENTS } from '../constants.js';
import type { IdFactory } from '../ids.js';
import type { Logger } from '../log.js';
import type { GapMarkerRecord, QueueRecord } from './types.js';

/**
 * The durable play-event queue (technical-design §11.6). Capped at 20,000 records;
 * on overflow the oldest are dropped and a gap marker takes their place, because a
 * silently short report reads as a low confirmation rate and credits money for
 * plays that happened.
 */

export interface QueuedRecord {
  readonly seq: number;
  readonly record: QueueRecord;
}

export interface EventQueueStore {
  append(record: QueueRecord): Promise<number>;
  /** Oldest first. */
  head(limit: number): Promise<readonly QueuedRecord[]>;
  delete(seqs: readonly number[]): Promise<void>;
  count(): Promise<number>;
}

export interface EventQueue {
  enqueue(record: QueueRecord): Promise<void>;
  /** Oldest first, up to `batchSize` (`settings.event_batch_size`). */
  peek(batchSize: number): Promise<readonly QueuedRecord[]>;
  /** Called only after the server acks (technical-design §11.6). */
  ack(seqs: readonly number[]): Promise<void>;
  size(): Promise<number>;
}

export interface EventQueueOptions {
  readonly store: EventQueueStore;
  readonly logger: Logger;
  readonly newId: IdFactory;
  readonly deviceNow: () => number;
  readonly clockUnsynced: () => boolean;
  readonly maxRecords?: number;
}

export function createEventQueue(options: EventQueueOptions): EventQueue {
  const maxRecords = options.maxRecords ?? EVENT_QUEUE_MAX_EVENTS;

  const dropOldest = async (screenId: string, howMany: number): Promise<void> => {
    const doomed = await options.store.head(howMany);
    if (doomed.length === 0) return;

    let dropped = 0;
    let from = Number.POSITIVE_INFINITY;
    let to = Number.NEGATIVE_INFINITY;
    const seqs: number[] = [];

    for (const entry of doomed) {
      seqs.push(entry.seq);
      if (entry.record.kind === 'play') {
        dropped += 1;
        from = Math.min(from, entry.record.started_at_device_ms);
        to = Math.max(to, entry.record.ended_at_device_ms);
      } else {
        // Collapse an older marker into the new one rather than losing its count.
        dropped += entry.record.dropped_events;
        from = Math.min(from, entry.record.from_device_ms);
        to = Math.max(to, entry.record.to_device_ms);
      }
    }

    await options.store.delete(seqs);

    const marker: GapMarkerRecord = {
      kind: 'gap',
      id: options.newId(),
      screen_id: screenId,
      dropped_events: dropped,
      from_device_ms: Number.isFinite(from) ? from : options.deviceNow(),
      to_device_ms: Number.isFinite(to) ? to : options.deviceNow(),
      recorded_at_device_ms: options.deviceNow(),
      clock_unsynced: options.clockUnsynced(),
    };
    await options.store.append(marker);
    options.logger.warn('events.queue_overflow', {
      dropped_events: dropped,
      gap_id: marker.id,
    });
  };

  return {
    async enqueue(record) {
      let count = await options.store.count();
      if (count >= maxRecords) {
        // Make room for the gap marker and the new record in one pass.
        const dropCount = count - maxRecords + 2;
        await dropOldest(record.screen_id, dropCount);
        count = await options.store.count();
      }
      if (count >= maxRecords) {
        const dropCount = count - maxRecords + 1;
        await dropOldest(record.screen_id, dropCount);
      }
      await options.store.append(record);
    },

    peek: (batchSize) => options.store.head(batchSize),

    ack: (seqs) => options.store.delete(seqs),

    size: () => options.store.count(),
  };
}

export function createMemoryEventQueueStore(): EventQueueStore {
  const rows = new Map<number, QueueRecord>();
  let nextSeq = 1;

  return {
    append: async (record) => {
      const seq = nextSeq;
      nextSeq += 1;
      rows.set(seq, record);
      return seq;
    },
    head: async (limit) =>
      [...rows.entries()]
        .sort((a, b) => a[0] - b[0])
        .slice(0, Math.max(0, limit))
        .map(([seq, record]) => ({ seq, record })),
    delete: async (seqs) => {
      for (const seq of seqs) rows.delete(seq);
    },
    count: async () => rows.size,
  };
}
